import { useEffect, useRef } from 'react'

interface UseInfiniteScrollSentinelOptions {
  /** Called when the sentinel enters the viewport AND `enabled` is true. */
  onIntersect: () => void
  /**
   * Gating predicate. Typically `hasNextPage && !isFetchingNextPage` so we don't trigger
   * back-to-back loads while one is still in flight.
   */
  enabled: boolean
  /**
   * Root-margin passed to `IntersectionObserver`. Default `200px` so we kick off the next
   * page slightly before the user reaches the bottom — smooths perceived loading.
   */
  rootMargin?: string
}

/**
 * Attach the returned ref to a sentinel `<div>` at the bottom of an infinite list. The hook
 * sets up an `IntersectionObserver` against the viewport; when the sentinel enters and the
 * `enabled` predicate is true, `onIntersect` fires (usually `fetchNextPage`).
 *
 * <p>Re-binds on every dependency change so a freshly-mounted sentinel after a page reset
 * still fires.
 */
export const useInfiniteScrollSentinel = ({
  onIntersect,
  enabled,
  rootMargin = '200px',
}: UseInfiniteScrollSentinelOptions) => {
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const node = ref.current
    if (!node || !enabled) return
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            onIntersect()
          }
        }
      },
      { rootMargin }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [onIntersect, enabled, rootMargin])

  return ref
}
